import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  TitleFolderAsset,
  IconFolderAsset,
  TitleFileAsset,
  IconFileAsset,
} from '../shared/components';

import { getInteropListItems } from 'services/interoperability';
import { formatRangeDateTime } from 'utils/formatDate';

const getUuidKey = () => uuidv4();

const getNodeTreeData = (interop, multipleImport) => {
  if (interop.type === 'folder') {
    return {
      key: getUuidKey(),
      title: <TitleFolderAsset title={interop.name} />,
      icon: <IconFolderAsset />,
      data: interop,
      selectable: false,
      checkable: multipleImport ? true : false,
    };
  }
  return {
    key: getUuidKey(),
    title: <TitleFileAsset title={interop.name} />,
    icon: <IconFileAsset />,
    data: interop,
    selectable: multipleImport ? false : true,
    isLeaf: true,
  };
};

export const updateTreeData = (list, key, children) => {
  return list.map((node) => {
    if (node.key === key) {
      return { ...node, children };
    }

    if (node.children) {
      return {
        ...node,
        children: updateTreeData(node.children, key, children),
      };
    }

    return node;
  });
};

export const mappingInteropItemsToTreeData = (
  interopItems = [],
  multipleImport = false
) => {
  let treeData = [];

  interopItems.forEach((interop) => {
    if (interop.type === 'folder') {
      let node = getNodeTreeData(interop, multipleImport);
      treeData.push(node);
    } else {
      let node = getNodeTreeData(interop, multipleImport);
      treeData.push(node);
    }
  });

  return treeData;
};

export const getExpandedData = async (params) => {
  const response = await getInteropListItems(params);
  if (response?.isSuccess) {
    if (response?.data?.interopItems) {
      return response?.data?.interopItems;
    }
  }
  return null;
};

export const getFormDataParams = (params = {}) => {
  let formData = new FormData();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        formData.append(key, item);
      });
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

export const filterFoldersAndFilesInSelectedItems = (selectedItems = []) => {
  let folders = [];
  let files = [];

  selectedItems.forEach((item) => {
    const { data } = item;

    if (data.type === 'file') {
      files.push(item);
    } else if (data.type === 'folder') {
      if (Array.isArray(item.children) && item.children.length > 0) {
        folders.push(item);
        filterFoldersAndFilesInSelectedItems(item.children);
      } else {
        folders.push(item);
      }
    }
  });

  return {
    folders,
    files,
  };
};

const findFileExistInAnyFolders = (folders, file) => {
  return folders.some((item) => {
    if (item.key === file.key) {
      return true;
    } else if (item.children) {
      return findFileExistInAnyFolders(item.children, file);
    }
    return false;
  });
};

// A file will be sent to the server when folder that contains the file does not check
export const getFilesPath = (folders, files) => {
  let result = [];

  files.forEach((file) => {
    const isExist = findFileExistInAnyFolders(folders, file);

    if (!isExist) {
      result.push(file.data.path);
    }
  });

  return result;
};

const findFolderExistInAnyFolders = (folders, findFolder) => {
  return folders.some((folder) => {
    if (folder.key === findFolder.key) {
      return true;
    } else if (folder.children) {
      return findFolderExistInAnyFolders(folder.children, findFolder);
    }
    return false;
  });
};

// A folder will be sent to the server when folder that contains the file does not check
export const getFoldersPath = (folders) => {
  let result = [];

  folders.forEach((folder) => {
    const filteredFolders = folders.filter((f) => f.key !== folder.key);

    const isExist = findFolderExistInAnyFolders(filteredFolders, folder);

    if (!isExist) {
      result.push(folder.data.path);
    }
  });

  return result;
};

let foundNode = null;
export const findNodeTree = (treeData, itemId) => {
  for (let index = 0; index < treeData.length; index++) {
    const node = treeData[index];
    if (node.data.itemId === itemId) {
      foundNode = node;
      break;
    }

    if (node?.children) {
      findNodeTree(node.children, itemId);
    }
  }
  return foundNode;
};

export const getChildNodesInFolder = (treeData) => {
  let children = [];
  const flattenMembers = treeData.map((m) => {
    if (m.children && m.children.length) {
      children = [...children, ...m.children];
    }
    return m;
  });

  return flattenMembers.concat(
    children.length ? getChildNodesInFolder(children) : children
  );
};

export const filterArrayInAnotherArray = (arr1, arr2) => {
  return arr1.filter((item) => !arr2.includes(item));
};

export const findAncestorsOfChildNode = (treeData, childNode) => {
  if (Array.isArray(treeData) && treeData.length > 0) {
    for (let i = 0; i < treeData.length; i++) {
      if (treeData[i].key === childNode.key) return [];

      let result = findAncestorsOfChildNode(treeData[i].children, childNode);
      if (result !== null) {
        result = result.concat(treeData[i]);
        return result;
      }
    }
  }
  return null;
};

export const filterArrayObjectInAnotherArrayObject = (
  array1 = [],
  array2 = []
) => {
  let result = [];

  array1.forEach((item1) => {
    const foundItem = array2.find((item2) => item1.key === item2.key);

    if (!foundItem) {
      result.push(item1);
    }
  });

  return result;
};

export const getOnlySelectedParentItems = (treeData, items, keys) => {
  let result = [];
  items.forEach((item) => {
    const ancestorNodes = findAncestorsOfChildNode(treeData, item);
    const ancestorKeys = ancestorNodes.map((node) => node.key);
    const isParentSelected = findSelectedKeyInAnotherKeys(keys, ancestorKeys);

    if (!isParentSelected) {
      result.push(item);
    }
  });
  return result;
};

export const findSelectedKeyInAnotherKeys = (keys, selectedKeys) => {
  return selectedKeys.find((key) => {
    return keys.includes(key);
  });
};

export const getFormValues = (form) => {
  const {
    effectivePeriod,
    defaultStatus,
    isCreateThumbs,
    autoApplyOrReplaceOnProducts,
  } = form.getFieldsValue();

  const startTime = formatRangeDateTime(effectivePeriod?.[0]);
  const endTime = formatRangeDateTime(effectivePeriod?.[1]);

  const formValues = {
    EffectedDate: startTime ? startTime.toISOString() : null,
    ExpirationDate: endTime ? endTime.toISOString() : null,
    defaultStatus,
    isCreateThumbs,
    autoApplyOrReplaceOnProducts,
  };

  return formValues;
};

export const getNodesByKeys = (treeData, keys) => {
  const result = [];

  const traverse = (nodes) => {
    for (const node of nodes) {
      if (keys.includes(node.key)) {
        result.push(node);
      }
      if (node.children) {
        traverse(node.children);
      }
    }
  };

  traverse(treeData);
  return result;
};
